import { IconButton } from "@/design-system/IconButton/IconButton";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { EllipsisVertical } from "lucide-react";
import {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

export type MenuItem = {
  /** Stable identity for the row; also the React key. */
  value: string;
  label: string;
  icon?: ReactNode;
  /**
   * The row reports something that is currently on. Drawn as a filled row
   * rather than a colour, so a menu of them says which are on without
   * implying good or bad.
   */
  selected?: boolean;
  disabled?: boolean;
  /** Hover text, typically why a disabled row is disabled. */
  title?: string;
  onSelect: () => void;
};

// Shared with menus that open on hover instead of a trigger
export const menuPanelClasses =
  "overflow-hidden rounded-[10px] border border-edge-strong bg-dropdown-background shadow-[0_18px_38px_rgba(0,0,0,0.55)]";

export const menuItemClasses = ({
  disabled,
  selected,
}: {
  disabled?: boolean;
  selected?: boolean;
}) =>
  cn(
    "flex w-full items-center gap-x-2.5 border-b border-edge px-3.5 py-2.5 text-left text-sm outline-none last:border-b-0",
    disabled
      ? "cursor-not-allowed text-foreground-disabled"
      : "text-foreground-body hover:bg-surface-raised focus-visible:bg-surface-raised",
    selected && !disabled && "bg-surface-raised text-foreground",
  );

type Props = {
  items: MenuItem[];
  /**
   * Replaces the built-in kebab. As a render prop it is handed the open state,
   * for a trigger that shows it.
   */
  trigger?: ReactNode | ((open: boolean) => ReactNode);
  /** Label for the built-in trigger. Ignored when `trigger` is set. */
  label?: string;
  /** Which edge of the trigger the panel lines up with. */
  align?: "start" | "end";
  /** Which way it opens. Use "top" where the trigger sits at the foot of a card. */
  placement?: "top" | "bottom";
  className?: string;
};

/**
 * A list of things to do, hung off its own trigger.
 *
 * This is not Dropdown, which picks a value out of a set and reports the one
 * chosen. Every row here runs something and closes, and a row may also report
 * a state it left behind (`selected`).
 *
 * The panel is positioned rather than portalled, so a clipping ancestor
 * (`overflow-hidden`, or a scroller) will cut it off: `placement` is what
 * keeps it inside the card it usually opens in.
 */
export const Menu = ({
  items,
  trigger,
  label,
  align = "end",
  placement = "bottom",
  className,
}: Props) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const root = useOutsideClick(close, open);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const menuId = useId();
  useEscape(close, open);

  const firstEnabled = items.findIndex((item) => !item.disabled);

  // Opening moves focus into the menu, which is what makes the arrow keys and
  // Escape reachable without a pointer.
  useEffect(() => {
    if (open) setActiveIndex(firstEnabled === -1 ? 0 : firstEnabled);
  }, [open, firstEnabled]);

  useEffect(() => {
    if (open) itemRefs.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  /** Next selectable row in `step`'s direction, wrapping at the ends. */
  const move = (step: number) => {
    if (items.length === 0) return;
    let next = activeIndex;
    for (let hops = 0; hops < items.length; hops++) {
      next = (next + step + items.length) % items.length;
      if (!items[next].disabled) break;
    }
    setActiveIndex(next);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      move(e.key === "ArrowDown" ? 1 : -1);
    }
    if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      setActiveIndex(e.key === "Home" ? firstEnabled : items.length - 1);
    }
  };

  const triggerNode =
    typeof trigger === "function" ? trigger(open) : (trigger ?? null);

  const toggle = (e: React.MouseEvent) => {
    // The menu is routinely placed inside a link or another clickable surface.
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <div className={cn("relative", className)} ref={root}>
      <span
        ref={triggerRef}
        className="contents"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
      >
        {triggerNode ?? (
          <IconButton
            icon={<EllipsisVertical className="size-4" />}
            aria-label={label ?? t("moreActions")}
            title={label ?? t("moreActions")}
            className="text-icon"
          />
        )}
      </span>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onKeyDown}
          className={cn(
            "absolute z-10 min-w-49",
            menuPanelClasses,
            align === "end" ? "right-0" : "left-0",
            placement === "top" ? "bottom-9" : "top-9",
          )}
        >
          {items.map((item, index) => (
            <button
              key={item.value}
              type="button"
              role="menuitem"
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              tabIndex={index === activeIndex ? 0 : -1}
              title={item.title}
              disabled={item.disabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                item.onSelect();
                close();
              }}
              className={menuItemClasses(item)}
            >
              {item.icon && <span className="shrink-0">{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
